"use client"

import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../lib/utils"

const choiceIndicatorVariants = cva(
	[
		"cursor-pointer shrink-0 rounded-xs size-6 text-xs font-extrabold flex items-center justify-center outline-none",
		"disabled:cursor-not-allowed disabled:opacity-50",
		"data-[state=unchecked]:bg-background data-[state=unchecked]:border-2 data-[state=unchecked]:border-input data-[state=unchecked]:text-muted-foreground",
		"data-[state=checked]:border-2 data-[state=checked]:border-[var(--choice-complement)] data-[state=checked]:text-[var(--choice-foreground)]",
		"data-[filled=true]:data-[state=checked]:bg-[var(--choice-complement)]",
		"data-[filled=true]:data-[state=checked]:shadow-[inset_0_0_0_3px_rgb(255_255_255)] dark:data-[filled=true]:data-[state=checked]:shadow-[inset_0_0_0_3px_hsl(var(--background))]",
		"focus-visible:ring-[var(--choice-complement)] focus-visible:ring-[3px]",
		"aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
	],
	{
		variants: {
			palette: {
				default: [
					"[--choice-foreground:var(--color-foreground)]",
					"[--choice-complement:var(--color-muted-foreground)]"
				],
				betta: ["[--choice-foreground:var(--color-betta)]", "[--choice-complement:var(--color-butterfly)]"],
				cardinal: ["[--choice-foreground:var(--color-cardinal)]", "[--choice-complement:var(--color-fire-ant)]"],
				bee: ["[--choice-foreground:var(--color-bee)]", "[--choice-complement:var(--color-lion)]"],
				owl: ["[--choice-foreground:var(--color-owl)]", "[--choice-complement:var(--color-tree-frog)]"],
				macaw: ["[--choice-foreground:var(--color-macaw)]", "[--choice-complement:var(--color-whale)]"]
			}
		},
		defaultVariants: {
			palette: "default"
		}
	}
)

interface ChoiceIndicatorProps extends VariantProps<typeof choiceIndicatorVariants> {
	letter?: string
	type?: "radio" | "checkbox"
	showLetter?: boolean
	className?: string
	id?: string
	value?: string
	checked?: boolean
	onCheckedChange?: (checked: boolean) => void
	disabled?: boolean
	palette?: "default" | "betta" | "cardinal" | "bee" | "owl" | "macaw"
}

function ChoiceIndicator({
	className,
	palette = "default",
	letter,
	type = "radio",
	showLetter = true,
	id,
	value,
	checked,
	onCheckedChange,
	disabled
}: ChoiceIndicatorProps) {
	const baseClassName = cn(choiceIndicatorVariants({ palette }), className)

	if (type === "checkbox") {
		return (
			<CheckboxPrimitive.Root
				data-slot="choice-indicator"
				data-palette={palette}
				data-filled={!showLetter ? "true" : undefined}
				className={baseClassName}
				id={id}
				checked={checked}
				onCheckedChange={onCheckedChange}
				disabled={disabled}
			>
				{showLetter && <span className="data-[state=unchecked]:block data-[state=checked]:hidden">{letter}</span>}
			</CheckboxPrimitive.Root>
		)
	}

	return (
		<RadioGroupPrimitive.Item
			data-slot="choice-indicator"
			data-palette={palette}
			className={baseClassName}
			id={id}
			value={value ?? ""}
			disabled={disabled}
		>
			{letter}
		</RadioGroupPrimitive.Item>
	)
}

export { ChoiceIndicator, choiceIndicatorVariants }
